import { Machine } from "xstate";
import { toaster } from "@/utilities/toaster";
import { ProjectModule } from "@/store/modules/project.module";
import { Guid } from "guid-typescript";
import { ClockWorkModule } from "@/store/modules/clockWork.module";
import { ProjectTagModule } from "@/store/modules/projectTag.module";
const projectDetailMachine = Machine({
    id: "projectDetail",
    context: {
        projectId: Guid.createEmpty(),
        user: "",
        taskId: null,
        linkId: null,
        tagOperationPerformed() { },
    },
    initial: "idle",
    states: {
        idle: {
            always: [{ target: "gettingDetails" }],
        },
        gettingDetails: {
            type: "parallel",
            states: {
                gettingProjectDetails: {
                    invoke: {
                        id: "gettingProjectDetails",
                        src: (context, event) => {
                            return ProjectModule.GetProjectDetailById(context.projectId);
                        },
                        onDone: { target: "#projectDetail.projectLoaded" },
                        onError: { target: "#projectDetail.serviceUnavailable" },
                    },
                },
                gettingTaskDetails: {
                    tags: "gettingTaskDetails",
                    invoke: {
                        id: "gettingTaskDetails",
                        src: (context, event) => {
                            return ProjectModule.GetTasksForProject(context.projectId);
                        },
                        onDone: { target: "#projectDetail.projectLoaded" },
                        onError: { target: "#projectDetail.serviceUnavailable" },
                    },
                },
            },
        },
        projectLoaded: {
            on: {
                UPDATE_PROJECT: { target: "updateProject" },
                CREATE_TASK: { target: "creatingTask" },
                MARK_AS_APEX_PROJECT: { target: "markingAsApexProject" },
                MARK_AS_APEX_SUB_PROJECT: { target: "markingAsApexSubProject" },
                REMOVE_AS_APEX_PROJECT: { target: "removingAsApexProject" },
                REMOVE_AS_APEX_SUB_PROJECT: { target: "removingAsApexSubProject" },
                EDIT_TASK: { target: "editTask" },
                DELETE_TASK: { target: "deleteTask" },
                ADD_MEMBER: { target: "gettingMembers" },
                CHANGE_OWNERSHIP: { target: "changeOwnership" },
                REMOVE_MEMBER_FROM_PROJECT: { target: "removingMemberFromProject" },
                LINK_WITH_PARENT_PROJECT: { target: "linkForParentOwnedProject" },
                REQUEST_FOR_USERS: { target: "gettingUser" },
                ADD_SUBPROJECT: { target: "addSubProject" },
                ASSIGN_TAG: { target: "assignTag" },
                UNASSIGN_TAG: { target: "unassignTag" },
                ADD_ENTRY_UNDER_TASK: { target: "addingEntryUnderTask", actions: "clearLiveTimer" },
                ADD_LINK: { target: "addLink" },
                EDIT_LINK: { target: "editLink" },
                DELETE_LINK: { target: "deleteLink" },
                ADDING_PROJECT_TO_WATCHLIST: { target: "addingProjectToWatchList" },
                REMOVING_PROJECT_FROM_WATCHLIST: { target: "removingProjectFromWatchList" },
                DELETE_SUB_PROJECT: { target: "deleteSubProject" },
                DELINK_SUB_PROJECT: { target: "deLinkSubProject" },
                CONVERT_TASK_TO_PROJECT: { target: "convertTaskToProject" },
                GETTING_OWNED_PROJECTS: { target: "gettingOwnedProjects" },
                DELETE_PROJECT: { target: "deleteProject" },
            },
        },
        gettingOwnedProjects: {
            invoke: {
                id: "gettingOwnedProjects",
                src: (context, event) => {
                    return ProjectModule.GettingOwnedProjects({ userId: event.userId, projectId: event.projectId });
                },
                onDone: { target: "selectProjectForMovingTask" },
                onError: { target: "projectLoaded" },
            },
        },
        selectProjectForMovingTask: {
            on: {
                MOVING_TASK_TO_PROJECT: { target: "movingTaskToProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        movingTaskToProject: {
            invoke: {
                id: "movingTaskToProject",
                src: (context, event) => {
                    return ProjectModule.MovedTaskToProject({ projectId: event.projectId, taskId: event.taskId });
                },
                onDone: { target: "projectLoaded", actions: "showSuccessMessage" },
                onError: { target: "projectLoaded" },
            },
        },
        updateProject: {
            on: {
                UPDATING_PROJECT: { target: "updatingProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        updatingProject: {
            invoke: {
                id: "updatingProject",
                src: (context, event) => {
                    return ProjectModule.UpdateProject(event.payload);
                },
                onDone: { target: "projectLoaded", actions: "showSuccessMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        markingAsApexProject: {
            invoke: {
                id: "markingAsApexProject",
                src: (context, event) => {
                    return ProjectModule.MarkAsApexProject(event.projectId);
                },
                onDone: { target: "projectLoaded", actions: "showApexAddedMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        markingAsApexSubProject: {
            invoke: {
                id: "markingAsApexSubProject",
                src: (context, event) => {
                    return ProjectModule.MarkAsApexProject(event.projectId);
                },
                onDone: { target: "projectLoaded", actions: "showApexAddedMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        removingAsApexProject: {
            invoke: {
                id: "removingAsApexProject",
                src: (context, event) => {
                    return ProjectModule.RemoveAsApexProject(event.projectId);
                },
                onDone: { target: "projectLoaded", actions: "showApexRemovedMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        removingAsApexSubProject: {
            invoke: {
                id: "removingAsApexSubProject",
                src: (context, event) => {
                    return ProjectModule.RemoveAsApexProject(event.projectId);
                },
                onDone: { target: "projectLoaded", actions: "showApexRemovedMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        deleteProject: {
            tags: "showDeleteProjectUI",
            on: {
                DELETING_PROJECT: { target: "deletingProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        deletingProject: {
            invoke: {
                id: "deletingProject",
                src: (context, event) => {
                    return ProjectModule.DeleteProjectById(event.projectId);
                },
                onDone: { target: "projectLoaded", actions: ["showSuccessMessage", "setDeletionSuccess"] },
                onError: { target: "projectLoaded", actions: "setDeletionError" },
            },
        },
        creatingTask: {
            on: {
                SAVING_TASK: { target: "savingTask" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        savingTask: {
            invoke: {
                id: "savingTask",
                src: (context, event) => {
                    return ProjectModule.CreateTaskUnderProject(event.payload);
                },
                onDone: { target: "projectLoaded", actions: "taskCreationDone" },
                onError: { target: "serviceUnavailable" },
            },
        },
        editTask: {
            on: {
                UPDATE_TASK: { target: "updatingTask" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        updatingTask: {
            invoke: {
                id: "updatingTask",
                src: (context, event) => {
                    return ProjectModule.UpdateTask(event.payLoad);
                },
                onDone: { target: "projectLoaded", actions: "showSuccessMessage" },
                onError: { target: "serviceUnavailable" },
            },
        },
        deleteTask: {
            tags: "showDeleteTaskUI",
            on: {
                DELETING_TASK: { target: "deletingTask" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        deletingTask: {
            tags: "showDeleteTaskUI",
            invoke: {
                id: "deletingTask",
                src: (context, event) => {
                    return ProjectModule.DeleteTask(context.taskId);
                },
                onDone: { target: "projectLoaded", actions: "showSuccessMessage" },
                onError: { target: "projectLoaded" },
            },
        },
        gettingMembers: {
            tags: "showMemberModal",
            invoke: {
                id: "gettingMembers",
                src: (context, event) => {
                    return ProjectModule.GetMembersForShareProject(context.projectId);
                },
                onDone: { target: "addingMembers" },
                onError: { target: "serviceUnavailable" },
            },
        },
        addingMembers: {
            tags: "showMemberModal",
            on: {
                SAVING_MEMBER: { target: "savingMember" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        savingMember: {
            tags: "showMemberModal",
            invoke: {
                id: "savingMember",
                src: (context, event) => {
                    return ProjectModule.AddProjectTeamMember(event.member);
                },
                onDone: { target: "projectLoaded", actions: "memberCreationDone" },
                onError: { target: "serviceUnavailable" },
            },
        },
        changeOwnership: {
            on: {
                SAVING_OWNERSHIP: { target: "changingOwnerShip" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        changingOwnerShip: {
            invoke: {
                id: "changingOwnerShip",
                src: (context, event) => {
                    return ProjectModule.updateOwner({ projectId: context.projectId, employeeId: event.employeeId });
                },
                onDone: { target: "#projectDetail.gettingDetails.gettingProjectDetails", actions: "changeOwnershipDone" },
                onError: { target: "serviceUnavailable" },
            },
        },
        usersLoaded: {
            tags: "showLinkUserCard",
            on: {
                REQUESTING_USER: { target: "requestingUserForLinkingProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        gettingUser: {
            tags: "showLinkUserCard",
            invoke: {
                id: "gettingUser",
                src: (context, event) => {
                    return ProjectModule.GetAllActiveUsersForParentProjects();
                },
                onDone: { target: "usersLoaded" },
                onError: { target: "serviceUnavailable" },
            },
        },
        requestingUserForLinkingProject: {
            invoke: {
                id: "requestingUserForParentProject",
                src: (context, event) => {
                    return ProjectModule.RequestToParentProject(event.payload);
                },
                onDone: { target: "projectLoaded", actions: "requestForParentProjectDone" },
                onError: { target: "serviceUnavailable" },
            },
        },
        removingMemberFromProject: {
            invoke: {
                id: "removingMemberFromProject",
                src: (context, event) => {
                    return ProjectModule.RemoveMemberFromProject({ projectId: context.projectId, employeeId: event.employeeId });
                },
                onDone: { target: "projectLoaded", actions: "removeMember" },
                onError: { target: "projectLoaded", actions: "showErrorMessage" },
            },
        },
        linkForParentOwnedProject: {
            invoke: {
                id: "linkForParentOwnedProject",
                src: (context, event) => {
                    return ProjectModule.GetOwnedParentProjects(event.projectId);
                },
                onDone: { target: "linkWithParentProject" },
                onError: { target: "serviceUnavailable" },
            },
        },
        linkWithParentProject: {
            on: {
                LINKING_PROJECT: { target: "linkingWithParentProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        linkingWithParentProject: {
            invoke: {
                id: "linkingWithParentProject",
                src: (context, event) => {
                    return ProjectModule.LinkSubprojectToOwnedProject(event.payLoad);
                },
                onDone: { target: "#projectDetail.gettingDetails.gettingProjectDetails", actions: "linkToSubProject" },
                onError: { target: "serviceUnavailable" },
            },
        },
        addSubProject: {
            on: {
                ADDING_SUBPROJECT: { target: "addingSubProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        addingSubProject: {
            invoke: {
                id: "addingSubProject",
                src: (context, eve) => {
                    return ProjectModule.CreateSubProject({ projectId: context.projectId, project: eve.payload });
                },
                onDone: { target: "projectLoaded", actions: "addSubProject" },
                onError: { target: "serviceUnavailable" },
            },
        },
        assignTag: {
            on: {
                ASSIGNING_TAG: { target: "assigningTag" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        assigningTag: {
            invoke: {
                id: "assigningTag",
                src: async (context, eve) => {
                    const response = await ProjectTagModule.ManageTag({ projectId: context.projectId, operation: eve.payload });
                    if (response.projectTags) {
                        ProjectModule.SetProjectTags(response.projectTags);
                    }
                    return response;
                },
                onDone: { target: "projectLoaded", actions: "tagOperationPerformed" },
                onError: { target: "assignTag" },
            },
        },
        unassignTag: {
            on: {
                UNASSIGNING_TAG: { target: "unassigningTag" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        unassigningTag: {
            invoke: {
                id: "unassigningTag",
                src: async (context, eve) => {
                    const response = await ProjectTagModule.ManageTag({ projectId: context.projectId, operation: eve.payload });
                    if (response.projectTags) {
                        ProjectModule.SetProjectTags(response.projectTags);
                    }
                    return response;
                },
                onDone: { target: "projectLoaded", actions: "tagOperationPerformed" },
                onError: { target: "unassignTag" },
            },
        },
        addingEntryUnderTask: {
            invoke: {
                id: "addingEntryUnderTask",
                src: (context, eve) => {
                    return ClockWorkModule.AddEntryUnderTask({ taskId: eve.taskId, taskName: eve.taskName });
                },
                onDone: { target: "projectLoaded", actions: "EntryCreationDone" },
                onError: { target: "serviceUnavailable" },
            },
        },
        deleteLink: {
            tags: "showDeleteLinkUI",
            on: {
                DELETING_LINK: { target: "deletingLink" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        deletingLink: {
            tags: "showDeleteLinkUI",
            invoke: {
                id: "deletingLink",
                src: (context, evt) => {
                    return ProjectModule.DeleteLinkById(context.linkId);
                },
                onDone: { target: "projectLoaded", actions: "deletingLink" },
                onError: { target: "serviceUnavailable" },
            },
        },
        editLink: {
            on: {
                UPDATE_LINK: { target: "updatingLink" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        updatingLink: {
            invoke: {
                id: "updateLink",
                src: (context, evt) => {
                    return ProjectModule.UpdateLink(evt.editFileLink);
                },
                onDone: { target: "projectLoaded", actions: "updateLink" },
                onError: { target: "editLink" },
            },
        },
        addLink: {
            on: {
                SAVE_LINK: { target: "savingLink" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        savingLink: {
            invoke: {
                id: "savingLink",
                src: (context, evt) => {
                    return ProjectModule.CreateLink({ projectFileLink: evt.link, projectId: context.projectId });
                },
                onDone: { target: "projectLoaded", actions: "addLink" },
                onError: { target: "serviceUnavailable" },
            },
        },
        addingProjectToWatchList: {
            invoke: {
                id: "addingProjectToWatchList",
                src: (context, evt) => {
                    return ProjectModule.AddProjectToWatchList(context.projectId);
                },
                onDone: { target: "projectLoaded", actions: "addToWatchListDone" },
                onError: { target: "serviceUnavailable" },
            },
        },
        removingProjectFromWatchList: {
            invoke: {
                id: "removingProjectFromWatchList",
                src: (context, evt) => {
                    return ProjectModule.RemoveProjectFromWatchList(context.projectId);
                },
                onDone: { target: "projectLoaded", actions: "removeFromWatchListDone" },
                onError: { target: "projectLoaded" },
            },
        },
        deleteSubProject: {
            tags: "showDeleteSubProjectUI",
            on: {
                DELETING_SUB_PROJECT: { target: "deletingSubProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        deletingSubProject: {
            tags: "showDeleteSubProjectUI",
            invoke: {
                id: "deletingSubProject",
                src: (context, eve) => {
                    return ProjectModule.DeleteSubProjectById(eve.projectId);
                },
                onDone: { target: "projectLoaded", actions: "showSuccessMessage" },
                onError: { target: "projectLoaded" },
            },
        },
        convertTaskToProject: {
            on: {
                CONVERTING_TASK_TO_PROJECT: { target: "convertingTaskToProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        convertingTaskToProject: {
            tags: "convertTaskToProject",
            invoke: {
                id: "convertTaskToProject",
                src: (context, eve) => {
                    return ProjectModule.ConvertTaskToProject(eve.payload);
                },
                onDone: { target: "projectLoaded", actions: "showSuccessMessage" },
                onError: { target: "convertTaskToProject" },
            },
        },
        deLinkSubProject: {
            tags: "showDeLinkSubProjectUI",
            on: {
                DELINKING_SUB_PROJECT: { target: "deLinkingSubProject" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        deLinkingSubProject: {
            tags: "showDeLinkSubProjectUI",
            invoke: {
                id: "deLinkingSubProject",
                src: (context, eve) => {
                    return ProjectModule.DeLinkSubProjectById(eve.projectId);
                },
                onDone: { target: "projectLoaded", actions: "deLinkSubProjectIsDone" },
                onError: { target: "projectLoaded" },
            },
        },
        serviceUnavailable: {
            type: "final",
        },
    },
}, {
    actions: {
        showErrorMessage: (context, event) => {
            toaster.ShowSuccess(event.data.errorMessages[0]);
        },
        showSuccessMessage: (context, event) => {
            toaster.ShowSuccess(event.data.successMessages[0]);
        },
        showApexAddedMessage: (context, event) => {
            toaster.ShowSuccess("Project is updated as Apex Project successfully.");
        },
        showApexRemovedMessage: (context, event) => {
            toaster.ShowSuccess("Project is removed as Apex Project successfully.");
        },
        taskCreationDone: () => {
            toaster.ShowSuccess("Task has been created successfully.");
        },
        memberCreationDone: () => {
            toaster.ShowSuccess("Team Member has been added successfully.");
        },
        changeOwnershipDone: () => {
            toaster.ShowSuccess("Project ownership has been changed successfully.");
        },
        removeMember: () => {
            toaster.ShowSuccess("Team Member has been removed successfully");
        },
        requestForParentProjectDone: () => {
            toaster.ShowSuccess("Request successfully send to the user.");
        },
        addSubProject: () => {
            toaster.ShowSuccess("Sub-Project has been created successfully.");
        },
        tagOperationPerformed: (context, event) => {
            if (context.tagOperationPerformed) {
                context.tagOperationPerformed();
                //toaster.ShowSuccess("tag assigned to all sub projects successfully.");
            }
        },
        linkToSubProject: () => {
            toaster.ShowSuccess("Project has been linked successfully.");
        },
        EntryCreationDone: () => {
            toaster.ShowSuccess("Time entry has been started successfully.");
        },
        addLink: () => {
            toaster.ShowSuccess("Link has been added successfully");
        },
        updateLink: () => {
            toaster.ShowSuccess("Link has been updated successfully");
        },
        deletingLink: () => {
            toaster.ShowSuccess("Link has been deleted successfully");
        },
        addToWatchListDone: () => {
            toaster.ShowSuccess("This Project has been added to the Watchlist successfully.");
        },
        removeFromWatchListDone: () => {
            toaster.ShowSuccess("This Project has been removed from the Watchlist successfully.");
        },
        deLinkSubProjectIsDone: () => {
            toaster.ShowSuccess("This Sub-Project has been de-linked successfully.");
        },
        clearLiveTimer: () => {
            ClockWorkModule.ResetLiveTimer();
        },
        moveTaskToProject: () => {
            toaster.ShowSuccess("Task has been moved successfully.");
        },
        setDeletionSuccess: (context) => {
            context.deletionResult = "success";
        },
        setDeletionError: (context) => {
            context.deletionResult = "error";
        },
    },
    guards: {},
    activities: {},
    services: {},
});
export default projectDetailMachine;
